<template>
  <div class="text-start">
    <v-dialog v-model="isOpen" width="800" :persistent="isEditing">
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-bind="attrs" v-on="on" @click="openDialog"> View </v-btn>
      </template>

      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          Medical Information
          <v-spacer />
          <v-btn v-if="!isEditing" icon @click="enterEditMode" :loading="loadingMedicines">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
        </v-card-title>

        <!-- ── READ-ONLY VIEW ── -->
        <template v-if="!isEditing">
          <div v-if="medicines.length === 0" class="pa-4 grey--text">No medicines added yet.</div>
          <div v-for="medicine in medicines" :key="medicine.id" class="pa-4">
            <div class="heading-6">
              {{ medicine.tradeName }} ({{ medicine.genericName }}) / {{ medicine.dosage }} / {{ medicine.detail }} x
              {{ medicine.quantity }}
            </div>
            <div class="overline">{{ medicine.howToTake }} {{ medicine.time }} {{ medicine.moreDetail }}</div>
          </div>
          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" text @click="isOpen = false"> Close </v-btn>
          </v-card-actions>
        </template>

        <!-- ── EDIT VIEW ── -->
        <template v-else>
          <v-card-text class="pt-4">
            <div
              v-for="(item, index) in form.medicines"
              :key="index"
              class="mb-4 pa-3"
              style="border: 1px solid #e0e0e0; border-radius: 4px"
            >
              <div class="d-flex align-center justify-space-between mb-1">
                <span class="subtitle-2">Medicine #{{ index + 1 }}</span>
                <v-btn icon small @click="removeMedicine(index)">
                  <v-icon small color="error">mdi-close</v-icon>
                </v-btn>
              </div>

              <!-- Live preview -->
              <div class="heading-6 grey--text text--darken-1 mb-1" v-if="item.selectedMedicine">
                {{ item.selectedMedicine.tradeName }} ({{ item.selectedMedicine.genericName }}) / {{ item.dosage }} x
                {{ item.quantity }}
              </div>
              <div class="overline grey--text mb-2">{{ item.howToTake }} {{ item.time }} {{ item.moreDetail }}</div>

              <!-- Medicine dropdown -->
              <v-autocomplete
                v-model="item.selectedMedicine"
                :items="masterMedicines"
                :item-text="(m) => `${m.tradeName} (${m.genericName}) - ${m.dosage}`"
                label="Medicine"
                return-object
                clearable
                @change="onMedicineChange(index)"
                class="mb-2"
              />

              <v-row dense>
                <v-col cols="6" sm="3">
                  <v-text-field v-model.number="item.quantity" label="Quantity" type="number" min="1" />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field v-model="item.dosage" label="Dosage / day" />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field v-model="item.howToTake" label="How to take" />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field v-model="item.time" label="Time to take" />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="item.moreDetail" label="More detail" />
                </v-col>
              </v-row>
            </div>

            <v-btn text color="primary" @click="addMedicine"> <v-icon left>mdi-plus</v-icon> Add Medicine </v-btn>
          </v-card-text>

          <v-divider />
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="cancelEdit">Cancel</v-btn>
            <v-btn text color="primary" :loading="saving" @click="save">Save</v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import numberUtil from '../../utils/number';
import PrescriptionService from '../../services/prescription';

export default {
  name: 'MedicineInfoDialog',
  props: {
    // Array of medicines from the current prescription document
    medicines: {
      type: Array,
      default: () => [],
    },
    // Identifies which prescription_details document to update
    prescriptionId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isOpen: false,
      isEditing: false,
      saving: false,
      loadingMedicines: false,
      // Full master catalogue of all available medicines from admin/medicines API call
      masterMedicines: [],
      form: {
        // Editable local copy of the current prescription's medicines
        medicines: [],
      },
    };
  },
  methods: {
    // Generates a random 9-character lowercase alphanumeric ID
    // matching the pattern of existing medicine IDs e.g. "1jhoffplp"
    generateId() {
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      return Array.from({ length: 9 }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join('');
    },

    openDialog() {
      this.isEditing = false;
    },

    async enterEditMode() {
      if (this.masterMedicines.length === 0) {
        await this.fetchMasterMedicines();
      }
      this.initForm();
      this.isEditing = true;
    },

    async fetchMasterMedicines() {
      this.loadingMedicines = true;
      try {
        await window
          .axios({
            method: 'GET',
            baseURL: process.env.VUE_APP_EPRES_ENDPOINT,
            url: 'admin/medicines',
          })
          .then((response) => {
            this.masterMedicines = response.data.data.map((medicine) => ({
              ...medicine,
              price: Number(medicine.price),
            }));
          });
      } catch (err) {
        console.error('Error fetching medicine masterlist:', err);
      } finally {
        this.loadingMedicines = false;
      }
    },

    // Converts read-only prop medicines into editable form rows with medicine masterlist reference
    initForm() {
      this.form.medicines = this.medicines.map((m) => {
        const selectedMedicine = this.masterMedicines.find((med) => med.genericName === m.genericName) || null;
        return {
          selectedMedicine,
          quantity: m.quantity ?? 1,
          dosage: m.dosage || '',
          howToTake: m.howToTake || '',
          time: m.time || '',
          moreDetail: m.moreDetail || '',
        };
      });
    },

    // Uses this.$set to replace the entire object at the index,
    // forcing Vue 2 reactivity to detect the change and re-render
    onMedicineChange(index) {
      const item = this.form.medicines[index];
      const med = item.selectedMedicine;
      if (!med) {
        this.$set(this.form.medicines, index, {
          ...item,
          dosage: '',
          howToTake: '',
          time: '',
          moreDetail: '',
        });
        return;
      }
      this.$set(this.form.medicines, index, {
        ...item,
        dosage: med.dosageTh || '',
        howToTake: med.howToTakeTh || '',
        time: med.timeTh || '',
        moreDetail: med.detailTh || '',
      });
    },

    addMedicine() {
      this.form.medicines.push({
        selectedMedicine: null,
        quantity: 1,
        dosage: '',
        howToTake: '',
        time: '',
        moreDetail: '',
      });
    },

    removeMedicine(index) {
      this.form.medicines.splice(index, 1);
    },

    // Structures form data into the exact shape required by the MongoDB prescription_details medicines array, sent via PATCH request
    buildPayload() {
      return {
        medicines: this.form.medicines
          .filter((m) => m.selectedMedicine)
          .map((m) => ({
            id: this.generateId(),
            quantity: parseInt(m.quantity, 10),
            dosage: m.dosage || '',
            howToTake: m.howToTake || '',
            time: m.time || '',
            moreDetail: m.moreDetail ?? null,
            phamarcistDetail: m.selectedMedicine.detailTh ?? '',
            genericName: m.selectedMedicine.genericName,
            tradeName: m.selectedMedicine.tradeName,
            amount: m.selectedMedicine.dosage,
            sellUnit: m.selectedMedicine.sellUnitTh,
            unit: m.selectedMedicine.unitTh,
            price: String(m.selectedMedicine.price),
            active: m.selectedMedicine.active,
          })),
      };
    },

    async save() {
      this.$root
        .$confirm('Update Medicines', 'Are you sure you want to update the medicines for this prescription?', {
          color: 'primary',
        })
        .then(async (confirm) => {
          if (!confirm) return;
          this.saving = true;
          try {
            const payload = this.buildPayload();
            await PrescriptionService.updatePrescriptionDetail({
              prescriptionId: this.prescriptionId,
              medicines: payload.medicines,
            })
              .then(() => {
                this.$notify({
                  title: 'Success',
                  text: 'Prescription medicines have been updated successfully.',
                  type: 'success',
                });
                this.$emit('success', payload.medicines);
                this.isEditing = false;
                this.isOpen = false;
              })
              .catch((error) => {
                console.error('Error saving prescription medicines:', error);
                this.$notify({
                  title: 'Error',
                  text: 'There was an error updating the prescription medicines. Please try again.',
                  type: 'error',
                });
              });
          } finally {
            this.saving = false;
          }
        });
    },

    cancelEdit() {
      this.isEditing = false;
    },
  },
};
</script>
